// ============================================================
// GradePresencaFisicaDupla.js — Grade de DUAS LINHAS por atleta
// (só no painel do coordenador, modo leitura).
//
// Linha 1: frequência do treino TÉCNICO (oficial, vinda do calculo.js
//          via mapaDiario).
// Linha 2: frequência do treino FÍSICO (informativa, marcada pelo
//          preparador). Cinza-claro nos dias SEM treino físico.
//
// Colunas = os dias de treino TÉCNICO (o coordenador pediu só esses).
// Não faz cálculo do técnico — recebe pronto. Só combina as duas fontes.
// ============================================================

import { Fragment } from "react";
import { CINZA_ESC } from "../tema";
import useIsMobile from "../useIsMobile";
import { Marca, LegendaDePresenca, CabecalhoDoDia, corDoPercentual, estilosDaGrade, NomeNaGrade } from "./Presenca";

// De que status do cálculo sai qual marca. O mapa está aqui e não em
// Presenca.js porque quem conhece os nomes do calculo.js é a grade; a peça
// só sabe desenhar.
const MARCA_TEC = {
  presenca:          "presente",
  competicao:        "competicao",
  justif_modalidade: "semTreino",
  justif_individual: "abonada",
  viagem:            "viagem",
  falta:             "falta",
  sem_treino:        "semTreino",
  // Dia de hoje antes das 18h30: já aparece na grade, mas ainda não conta
  // como falta, e não pode ficar igual a feriado.
  aguardando:        "aguardando",
};

const diaLabel = iso => {
  const dt = new Date(iso + "T12:00:00");
  const semana = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"][dt.getDay()];
  return { num: String(dt.getDate()).padStart(2, "0"), semana };
};

// atletas: [{ nome, modalidade, mapaTecnico: {iso: {status,subtipo}}, pctTec }]
// diasTreino: array de ISO (dias de treino técnico)
// diasFisicoSemana: { modLower: [1..5] } — dias da semana com treino físico
// presencaFisica: { "chave__iso": "presente"|"falta" }
// chaveDe: função (atleta) -> chave usada na presencaFisica
export default function GradePresencaFisicaDupla({ atletas, diasTreino, diasFisicoSemana, presencaFisica, chaveDe }) {
  const estreito = useIsMobile();
  const GRADE = estilosDaGrade(estreito);
  if (!atletas || atletas.length === 0) {
    return <div style={{ fontSize: 13, color: CINZA_ESC }}>Nenhum atleta com dados neste período.</div>;
  }

  // Um dia é de treino físico se o dia-da-semana está no padrão da modalidade.
  function ehDiaFisico(modLower, iso) {
    const dias = diasFisicoSemana[modLower] || [];
    const dow = new Date(iso + "T12:00:00").getDay();
    return dias.includes(dow);
  }

  // % físico do atleta = presenças físicas ÷ dias de treino físico (entre os
  // dias de treino técnico exibidos, até o fim da lista).
  function pctFisico(a) {
    const modLower = (a.modalidade || "").toLowerCase();
    const diasFis = diasTreino.filter(iso => ehDiaFisico(modLower, iso));
    if (diasFis.length === 0) return null; // sem treino físico configurado
    const pres = diasFis.filter(iso => presencaFisica[`${chaveDe(a)}__${iso}`] === "presente").length;
    return pres / diasFis.length;
  }

  const ordenados = [...atletas].sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "pt-BR"));

  // "Técnico" e "Físico" na lateral de cada par de linhas. No celular o
  // rótulo encolhe junto com o resto: é o que menos precisa de espaço,
  // porque só tem dois valores e eles se alternam.
  const rotuloDaLinha = {
    padding: estreito ? "2px 3px" : "2px 6px",
    fontSize: estreito ? 8.5 : 10,
    color: CINZA_ESC, whiteSpace: "nowrap", fontWeight: 700,
    letterSpacing: 0.4, textTransform: "uppercase",
  };

  return (
    <div>
      <div style={GRADE.caixa}>
        <table style={GRADE.tabela}>
          <thead>
            <tr style={GRADE.cabecalho}>
              <th style={GRADE.thNome}>Atleta</th>
              <th style={{ ...GRADE.thDia, minWidth: 0, padding: estreito ? "8px 3px" : "8px 6px", whiteSpace: "nowrap" }} />
              {diasTreino.map(iso => {
                const { num, semana } = diaLabel(iso);
                return (
                  <th key={iso} style={GRADE.thDia}>
                    <CabecalhoDoDia num={num} semana={semana} />
                  </th>
                );
              })}
              <th style={GRADE.thPct}>%</th>
            </tr>
          </thead>
          <tbody>
            {ordenados.map((a, idx) => {
              const modLower = (a.modalidade || "").toLowerCase();
              const pFis = pctFisico(a);
              const bordaTop = idx > 0 ? "2px solid #E5E7EB" : "none";
              return (
                <Fragment key={a.nome}>
                  {/* LINHA 1 — treino técnico */}
                  <tr key={a.nome + "_tec"} style={{ borderTop: bordaTop }}>
                    <td rowSpan={2} style={{ ...GRADE.tdNome, borderTop: bordaTop }}>
                      <NomeNaGrade nome={a.nome} apoio={a.modalidade} estreito={estreito} />
                    </td>
                    <td style={rotuloDaLinha}>{estreito ? "Téc" : "Técnico"}</td>
                    {diasTreino.map(iso => {
                      const info = (a.mapaTecnico && a.mapaTecnico[iso]) || { status: "sem_treino" };
                      return (
                        <td key={iso} style={GRADE.tdDia}>
                          <Marca tipo={MARCA_TEC[info.status] || "semTreino"} estreito={estreito}
                            titulo={`${diaLabel(iso).num} — ${info.subtipo || info.status}`} />
                        </td>
                      );
                    })}
                    <td style={{ ...GRADE.tdPct, color: corDoPercentual(a.pctTec || 0) }}>
                      {Math.round((a.pctTec || 0) * 100)}%
                    </td>
                  </tr>
                  {/* LINHA 2 — treino físico */}
                  <tr key={a.nome + "_fis"}>
                    <td style={rotuloDaLinha}>{estreito ? "Fís" : "Físico"}</td>
                    {diasTreino.map(iso => {
                      const dia = diaLabel(iso).num;
                      if (!ehDiaFisico(modLower, iso)) {
                        return (
                          <td key={iso} style={GRADE.tdDia}>
                            <Marca tipo="semTreino" estreito={estreito} titulo={`${dia} — sem treino físico`} />
                          </td>
                        );
                      }
                      const st = presencaFisica[`${chaveDe(a)}__${iso}`];
                      const tipo = st === "presente" ? "presente" : st === "falta" ? "falta" : "naoMarcado";
                      return (
                        <td key={iso} style={GRADE.tdDia}>
                          <Marca tipo={tipo} estreito={estreito} titulo={`${dia} — treino físico`} />
                        </td>
                      );
                    })}
                    <td style={{ ...GRADE.tdPct, color: pFis === null ? "#CBD5E1" : corDoPercentual(pFis) }}>
                      {pFis === null ? "—" : Math.round(pFis * 100) + "%"}
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <LegendaDePresenca tipos={[
        { tipo: "presente" },
        { tipo: "competicao" },
        { tipo: "abonada" },
        { tipo: "falta" },
        { tipo: "semTreino", rotulo: "Sem treino" },
        { tipo: "naoMarcado", rotulo: "Físico não marcado" },
      ]} />
    </div>
  );
}
